import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  maintenancePlansApi,
  type ListMaintenancePlansFilters,
} from './service';
import type {
  MaintenancePlanCreateValues,
  MaintenancePlanUpdateValues,
  PlanTriggerCreateValues,
} from './schema';
import type { MaintenancePlan, PlanTrigger } from './types';

/**
 * Query key factory. Plans and their triggers share the root key so
 * that invalidating `all` after a mutation refreshes both surfaces
 * (a deleted trigger may shift the plan's lastGeneratedAt etc.).
 */
export const maintenancePlanKeys = {
  all: ['maintenancePlans'] as const,
  lists: () => [...maintenancePlanKeys.all, 'list'] as const,
  list: (filters: ListMaintenancePlansFilters) =>
    [...maintenancePlanKeys.lists(), filters] as const,
  detail: (id: string) =>
    [...maintenancePlanKeys.all, 'detail', id] as const,
  triggers: (planId: string) =>
    [...maintenancePlanKeys.all, 'triggers', planId] as const,
};

export function useMaintenancePlans(
  filters: ListMaintenancePlansFilters = {},
) {
  return useQuery({
    queryKey: maintenancePlanKeys.list(filters),
    queryFn: () => maintenancePlansApi.list(filters),
  });
}

export function useMaintenancePlan(id: string | undefined) {
  return useQuery({
    queryKey: id ? maintenancePlanKeys.detail(id) : maintenancePlanKeys.all,
    queryFn: () => maintenancePlansApi.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateMaintenancePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MaintenancePlanCreateValues) =>
      maintenancePlansApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenancePlanKeys.lists() });
    },
  });
}

export function useUpdateMaintenancePlan(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MaintenancePlanUpdateValues) =>
      maintenancePlansApi.update(id, body),
    onSuccess: (updated: MaintenancePlan) => {
      qc.invalidateQueries({ queryKey: maintenancePlanKeys.lists() });
      qc.setQueryData(maintenancePlanKeys.detail(id), updated);
    },
  });
}

export function useSetMaintenancePlanActive(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (isActive: boolean) =>
      maintenancePlansApi.setActive(id, isActive),
    onSuccess: (updated: MaintenancePlan) => {
      qc.invalidateQueries({ queryKey: maintenancePlanKeys.lists() });
      qc.setQueryData(maintenancePlanKeys.detail(id), updated);
    },
  });
}

export function useDeleteMaintenancePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => maintenancePlansApi.remove(id),
    onSuccess: (_void, id) => {
      qc.invalidateQueries({ queryKey: maintenancePlanKeys.all });
      qc.removeQueries({ queryKey: maintenancePlanKeys.detail(id) });
    },
  });
}

export function usePlanTriggers(planId: string | undefined) {
  return useQuery({
    queryKey: planId
      ? maintenancePlanKeys.triggers(planId)
      : maintenancePlanKeys.all,
    queryFn: () => maintenancePlansApi.listTriggers(planId as string),
    enabled: Boolean(planId),
  });
}

export function useCreatePlanTrigger(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PlanTriggerCreateValues) =>
      maintenancePlansApi.createTrigger(planId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenancePlanKeys.triggers(planId) });
    },
  });
}

export function useSetPlanTriggerActive(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { triggerId: string; isActive: boolean }) =>
      maintenancePlansApi.setTriggerActive(args.triggerId, args.isActive),
    onSuccess: (updated: PlanTrigger) => {
      qc.invalidateQueries({
        queryKey: maintenancePlanKeys.triggers(updated.planId),
      });
      qc.invalidateQueries({
        queryKey: maintenancePlanKeys.triggers(planId),
      });
    },
  });
}

export function useDeletePlanTrigger(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (triggerId: string) =>
      maintenancePlansApi.removeTrigger(triggerId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenancePlanKeys.triggers(planId) });
    },
  });
}
